import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { loadAsset } from '../../utils/AddressableManager';
import { PALEO_EARTH_FRAMES } from '../../utils/PaleoEarthAssets';
import { ERA_COLORS, ERA_ABBR, getEraLabel, buildFrameBasedSegments } from '../../utils/GeologicEras';

export default function PaleoEarthTimeline({
  timelineImages,
  onTextureReady,
  visible = true 
}) {
  const paleoFrames = PALEO_EARTH_FRAMES;
  const lastIndex = paleoFrames.length - 1;

  const [index, setIndex] = useState(lastIndex);
  const [isDragging, setIsDragging] = useState(false);
  const [isFrameLoading, setIsFrameLoading] = useState(false);
  const markerRatio = index / lastIndex;
  const MARKER_WIDTH_REM = 0.8;
  const MARKER_HALF_REM = MARKER_WIDTH_REM / 2;
  const END_CAP_REM = 0.75; // NEW — dedicated breathing room at each end,
                            // independent of marker width. This is what
                            // reserves extra visible track/image at the
                            // extremes, beyond the bare minimum needed
                            // to avoid clipping the marker.

  // Total inset the marker's CENTER travels within = half its own width
  // PLUS the end-cap. These stay as two separate named values on purpose —
  // combining them into one hand-typed number is exactly how the last bug
  // happened (two things that need to agree got typed independently).
  const MARKER_INSET_REM = MARKER_HALF_REM + END_CAP_REM;
  const markerLeftCss = `calc(${MARKER_INSET_REM}rem + (100% - ${MARKER_INSET_REM * 2}rem) * ${markerRatio})`;

  const eraSegments = useMemo(() => buildFrameBasedSegments(paleoFrames), [paleoFrames]);

  const trackRef = useRef(null);
  const loadTokenRef = useRef(0);
  


  // NEW — replaces the setTimeout debounce entirely. Holds only the most
  // recently REQUESTED index; the rAF loop below drains it at most once
  // per rendered frame, so a fast drag can never queue up more texture
  // swaps than the screen can actually show.
  const pendingIndexRef = useRef(null);
  const rafIdRef = useRef(null);

  const commitFrame = useCallback((frameIndex) => {
    const frame = paleoFrames[frameIndex];
    if (!frame) return;

    const myToken = ++loadTokenRef.current;
    setIsFrameLoading(true);

    loadAsset('texture', frame.key, frame.url)
      .then((texture) => {
        if (myToken !== loadTokenRef.current) return; // a newer frame won the race, drop this one
        setIsFrameLoading(false);
        onTextureReady(texture, frame);
      })
      .catch((err) => {
        console.error('[PaleoEarthTimeline] Failed to load frame:', frame.filename, err);
        if (myToken === loadTokenRef.current) setIsFrameLoading(false);
      });
  }, [paleoFrames, onTextureReady]);

  // NEW — the rAF drain loop. Runs continuously while there's a pending
  // request; each tick applies AT MOST the latest requested index, then
  // clears the pending slot. Any indices that arrived and were overwritten
  // in between ticks are silently skipped — that's the intended behavior,
  // not a bug: skipping intermediate frames during a fast drag is what
  // keeps the GPU from falling behind.
  const scheduleCommit = useCallback((frameIndex) => {
    pendingIndexRef.current = frameIndex;
    if (rafIdRef.current != null) return; // a drain tick is already scheduled

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;
      if (pendingIndexRef.current != null) {
        commitFrame(pendingIndexRef.current);
        pendingIndexRef.current = null;
      }
    });
  }, [commitFrame]);

  useEffect(() => {
    commitFrame(lastIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const indexFromClientX = useCallback((clientX) => {
    const track = trackRef.current;
    if (!track) return index;
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return Math.round(ratio * lastIndex);
  }, [index, lastIndex]);

  const handlePointerDown = (e) => {
    setIsDragging(true);
    const newIndex = indexFromClientX(e.clientX);
    setIndex(newIndex);
    scheduleCommit(newIndex);
    e.target.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!isDragging) return;
    const newIndex = indexFromClientX(e.clientX);
    setIndex((prev) => {
      if (newIndex !== prev) scheduleCommit(newIndex);
      return newIndex;
    });
  };

  const handlePointerUp = (e) => {
    setIsDragging(false);
    e.target.releasePointerCapture?.(e.pointerId);
    // Cancel any pending rAF-scheduled tick and force the FINAL settled
    // index to apply immediately — guarantees the frame you actually let
    // go on is the one that ends up showing, not whatever the throttle
    // happened to catch last.
    if (rafIdRef.current != null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    pendingIndexRef.current = null;
    commitFrame(index);
  };

  useEffect(() => () => {
    if (rafIdRef.current != null) cancelAnimationFrame(rafIdRef.current);
  }, []);

  if (!visible) return null;

  const currentFrame = paleoFrames[index];
  const eraLabel = getEraLabel(currentFrame.ageMa);
  const ageLabel = currentFrame.ageMa === 0 ? 'Present Day' : `${currentFrame.ageMa} MYA`;
  // const handlePositionPct = (index / lastIndex) * 100;

  return (
    <div
      style={{
        position: 'fixed',
        top: 'calc(1.5rem + env(safe-area-inset-top))',
        left: '50%',
        transform: 'translateX(-50%)',
        // width: 'min(720px, 90vw)',
        width: 'min(820px, 94vw)',
        zIndex: 10000,
        fontFamily: 'Orbitron, sans-serif',
        color: '#e7dbb9',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <div style={{ textAlign: 'center', marginBottom: '0.5rem', fontSize: '0.9rem' }}>
        <span style={{ fontWeight: 700 }}>{eraLabel}</span>
        {' — '}
        <span style={{ opacity: isFrameLoading ? 0.5 : 1 }}>
          {ageLabel}{isFrameLoading ? ' (loading…)' : ''}
        </span>
      </div>
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'relative',
          height: '3.5rem',
          borderRadius: '1.15rem',
          overflow: 'hidden',
          cursor: 'grab',
          touchAction: 'none',
          background: '#25251f',
          border: '0.11rem solid rgba(231, 219, 185, 0.9)',
          boxShadow: `
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 -8px 20px rgba(0,0,0,0.18),
            0 8px 30px rgba(0,0,0,0.25)
          `,
        }}
      >
        {/* Geological layers */}
        {eraSegments.map((seg, i) => {
          const eraImage = timelineImages?.[seg.name.toLowerCase()];
          const imageSource = eraImage?.image?.src;

          return (
            <div
              key={`${seg.name}-${seg.startIndex}`}
              title={seg.name} // native tooltip — works even in the 'hidden' tier
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${seg.leftPct}%`,
                width: `${seg.widthPct}%`,
                background: ERA_COLORS[seg.name],
                // backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.12), rgba(0,0,0,0.10))`,
                borderRight: i < eraSegments.length - 1 ? '1px solid rgba(20,20,15,0.35)' : 'none',
                transition: 'filter 150ms ease',
              }}
            >
              {eraImage && (
                <img
                  src={eraImage.src || eraImage}
                  alt=""
                  draggable={false}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                    userSelect: 'none',
                  }}
                />
              )}
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: ERA_COLORS[seg.name],
                  // opacity: 0.2,
                  pointerEvents: 'none',
                }}
              />
              <span style={{
                position: 'absolute', inset: 0, display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                color: 'rgba(255, 255, 255, 1)', fontSize: '0.8rem', fontWeight: 600,
                letterSpacing: '0.04em', textShadow: '0 1px 3px rgba(0, 0, 0, 0.95)',
                whiteSpace: 'nowrap', overflow: 'hidden', padding: '0 2px',
                }}>
                  {/* {seg.name} */}
                  {seg.widthPct < 12
                    ? ERA_ABBR[seg.name]
                    : seg.name
                  }
              </span>
            </div>
          );
        })}

        {/* Current-time glow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: markerLeftCss,
            width: '5rem',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            background:
              'radial-gradient(ellipse, rgba(255, 245, 200, 0.65), transparent 70%)',
            filter: 'blur(8px)',
          }}
        />

      </div>
      <svg
        style={{
          position: 'absolute',
          top: '54%',
          left: markerLeftCss,
          transform: 'translate(-50%, -50%)',
          // width: '1.5rem',
          width: `${MARKER_WIDTH_REM}rem`,
          height: '4.5rem',
          overflow: 'visible',
          pointerEvents: 'none',
          zIndex: 10,
        }}
        viewBox="0 0 12 100"
      >
        <defs>
          <linearGradient id="markerGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#fff9dc" />
            <stop offset="45%" stopColor="#e7dbb9" />
            <stop offset="100%" stopColor="#b9aa83" />
          </linearGradient>

          <filter id="markerGlow" x="-100%" y="-20%" width="300%" height="140%">
            <feGaussianBlur stdDeviation="1.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <path
          d="
            M 6 0
            L 11 8
            L 11 92
            L 6 100
            L 1 92
            L 1 8
            Z

            M 6 7
            L 8 12
            L 8 88
            L 6 93
            L 4 88
            L 4 12
            Z
          "
          fill="url(#markerGradient)"
          fillRule="evenodd"
          stroke="rgba(255,250,225,1)"
          strokeWidth="0"
          filter="url(#markerGlow)"
        />
      </svg>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', opacity: 0.6, marginTop: '0.25rem' }}>
        <span>750 Mya</span>
        <span>Today</span>
      </div>
    </div>
  );
}